// @ts-strict-ignore
import { HostListener, Input, ViewContainerRef, Directive, Output, EventEmitter, inject } from '@angular/core';
import {
  ComponentType,
  SohoApplicationMenuComponent,
} from 'ids-enterprise-ng';
import { LMDirtyCheckDialogComponent } from '@modal/lm-dirty-check-dialog.component';
import { LMBusyIndicatorService } from '@services/lm-busyindicator.service';
import { LMHtmlPageUtilService } from '@html/lm-html-page-util.service';
import { ListResource } from '@resources/list.resource';
import { BehaviorSubject, from, lastValueFrom, Observable } from 'rxjs';
import { LMContextService } from '@services/lm-context.service';
import { LMDialogButtonKey, LMFormType, LMUniqueIdService } from '@utils/index';
import { LMComponentInfoService } from '@services/lm-componentinfo.service';
import { LMCtrlShiftClickEvent } from './lm-ctrl-shift-click-event';
import { LMAppComponentService } from '@utils/lm-app-component.service';
import { ActivatedRoute, ActivatedRouteSnapshot, UrlSegment } from '@angular/router';
import {
  LMModalDialogWrapperRef,
  LMModalOpenService,
  LMModalWrapperService,
  LMRouteReuseCacheService
} from '@app/services';
import { LMBaseFormComponent } from '@app/components';

export interface LMDialogInfo {
  name?: string;
  message?: string;
  title?: string;
  type?: LMDialogType
}

export enum LMDialogType {
  NOYES = 'NoYes',
  LEAVESTAY = 'LeaveStay',
  APPLYDISCARD = 'ApplyDiscard'
}

@Directive()
export abstract class LMBaseComponent {

  protected dialog: LMModalDialogWrapperRef<any>;
  private baseCompId: string;

  private m_componentDestroyed = false;

  @HostListener('click', [ '$event' ]) onClick(event) {
    if ((this.lmComponentInfoService.getCardviewSpec() || (event.ctrlKey || event.metaKey) && event.shiftKey)) {
      // if we are on a field and we have click bweyond the bounds of that field stop this field info proccessing
      // and allow the event to bubble to the form.
      const element = event.currentTarget;
      if (element) {
        const fieldElements = Array.prototype.filter.call(element.children, function(kids) {
          return kids.matches('div[lm-field-options]');
        });
        const fieldElement = fieldElements.length > 0 ? fieldElements[0] : undefined;

        // check to see if this is field and that it is clicking beyond the field on the form itself
        if (fieldElement) {
          const rect = fieldElement.getBoundingClientRect();
          const right = rect.right;
          if (fieldElement && event.x > right) {
            return;
          }
        }
      }

      event.stopPropagation();
      this.displayComponentInfo(event, this);
    }
  }

  @Input() placeHolder: ViewContainerRef;
  @Output() appMenuTriggerEvent: EventEmitter<MouseEvent> = new EventEmitter<MouseEvent>();

  protected constructor(
    protected lmBusyIndicatorService: LMBusyIndicatorService,
    protected lmComponentInfoService: LMComponentInfoService,
    protected lmUniqueIdService: LMUniqueIdService,
    protected lmAppComponentService: LMAppComponentService,
    protected lmModalWrapperService: LMModalWrapperService,
    protected lmModalOpenService: LMModalOpenService
  ) {
  }

  public legacyWebUI = false;
  public url3_5 = undefined;
  public unsupportedPattern = false;
  private userWebRequestId = '';

  public uiCompare = false;

  // This is needed to connect the menu to its action. When the banner is in route the titlebar is loaded each time instead of just once like
  // it does when the banner is not in route. We run this when a screen is loaded to make sure the application menu button is connected to its action.
  public connectAppMenuButtonTrigger() {
    const menuComp = this.lmAppComponentService.getAppComponent().getSohoApplicationMenuComponent();
    if (menuComp) {
      setTimeout(() => {
        if (menuComp instanceof SohoApplicationMenuComponent) {
          menuComp.triggers = [ '.application-menu-trigger' ];
        }
      }, 1);
    }
  }

  public onAppMenuTriggerEvent(event: MouseEvent) {
    this.appMenuTriggerEvent.emit(event);
  }

  public abstract getComponentInfo(additionInfo?: LMCompInfoAdditionalInfo): LMComponentInfo | undefined;

  public abstract getComponentPlaceHolder(): ViewContainerRef | undefined;
  public abstract getUserWebRequestId(): string;

  public displayComponentInfo(event: LMCtrlShiftClickEvent, component?: LMBaseComponent): void {

    if ((!((event.ctrlKey || event.metaKey) && event.shiftKey)) && !event.isPage && !this.lmComponentInfoService.getCardviewSpec()) {
      return;
    }

    const placeHolder = this.getComponentPlaceHolder();
    const compInfo: LMComponentInfo = this.getComponentInfo(event.additionInfo);

    // allow fields to continue toward display. Block forms when the Comp type is List
    // const typeFromEvent: string = this.lmComponentInfoService.getComponentTypeFromEvent(event);
    // if (!(compInfo.componentType === 'FIELD' && compInfo.specField === 'NONSPECFIELD')
    // && (typeFromEvent !== null && ((compInfo.componentType === 'FORM' && typeFromEvent.indexOf('FORM') < 0 && compInfo.type !== 'Search'))
    // && compInfo.componentType !== typeFromEvent)) {
    //   return;
    // }

    compInfo.isPage = (compInfo.componentType !== 'FIELD' && (this.lmComponentInfoService.closest(event.srcElement, 'lm-page')));
    compInfo.routerComponent = component;
    this.lmComponentInfoService.displayComponentInfo(event, placeHolder, compInfo);
    // this.displayDialog(placeHolder, LMComponentInfoComponent, buttons, compInfo, 'Component Info');
  }

  // Handle unsaved changes. Return a boolean Promise that resolves when an async confirmation dialog completes.
  // The boolean indicates if navigation should proceed (true) or be cancelled (false).
  public allowPageNavigation(): Promise<boolean> {
    return Promise.resolve(true);
  }

  public getBaseCompId() {
    if (!this.baseCompId) {
      this.baseCompId = 'WRID-' + this.lmUniqueIdService.getCompWebUniqueId();
    }
    return this.baseCompId;
  }

  public getCompNameFromRoute(route: ActivatedRouteSnapshot, failedStr: string): string {
    // get the name from the route if possible
    const segments: UrlSegment[] = route.url;
    if (segments.length > 3) {
     const fourthSegment: string = segments[3].path;
     const fourthParts = fourthSegment.split(".");
     return fourthParts.length > 1 ? fourthParts[1] : failedStr;
    } else {
      return failedStr;
    }
  }

  public getRouteCompUserWebRequestId(): string {
    const appComp = this.lmAppComponentService.getAppComponent();
    if (!appComp) {
      return Soho.Locale.translate('NoComp');
    }
    const routeComp = appComp.getRouteComponent();
    if (!routeComp) {
      return Soho.Locale.translate('NoRouteComp');
    }

    return routeComp.getUserWebRequestId();
  }

  public showUnsavedChanges(info: LMDialogInfo): Observable<UnsavedChangesResponse> {
    const youMayNavigateSource = new BehaviorSubject<UnsavedChangesResponse>(LMDialogButtonKey.CANCEL);
    const youMayNavigate: Observable<UnsavedChangesResponse> = youMayNavigateSource.asObservable();

    const dialogId = 'UnsavedChanges';
    const type = info.type ?? LMDialogType.NOYES;

    let buttonConfig: any;
    if (type === LMDialogType.LEAVESTAY) {
      buttonConfig = { first: LMDialogButtonKey.LEAVE, second: LMDialogButtonKey.STAY };
    } else if (type === LMDialogType.APPLYDISCARD) {
      buttonConfig = { first: LMDialogButtonKey.APPLY, second: LMDialogButtonKey.DISCARDCHANGES };
    } else {
      buttonConfig = { first: LMDialogButtonKey.NO, second: LMDialogButtonKey.YES };
    }

    const buttons: SohoModalButton[] = [
      {
        id:    this.lmUniqueIdService.uniqueId([ dialogId, buttonConfig.first ]),
        text:  Soho.Locale.translate(buttonConfig.first),
        click: (e, modal) => {
          this.dialog.close(buttonConfig.first);
          this.hideBusyIndicator();
        },
      },
      {
        id:    this.lmUniqueIdService.uniqueId([ dialogId, buttonConfig.second ]),
        text:  Soho.Locale.translate(buttonConfig.second),
        click: (e, modal) => {
          this.dialog.close(buttonConfig.second);
        },
        isDefault: true
      }
    ];

    const title = (info.title) ? info.title : Soho.Locale.translate('UnsavedChangesMessage');
    if (!info.message) {
      info.message = (type === LMDialogType.LEAVESTAY) ? Soho.Locale.translate('NavigateAwayMessage') : Soho.Locale.translate('UnsavedChangesContinuingMsg');
    }

    const attributes: SohoAttributesArray = [ {
      name: 'data-automation-id',
      value: `lm-unsaved-changes-modal`
    } ];

    const options: SohoModalOptions = {
      fullsize: 'responsive',
      showCloseBtn: type !== LMDialogType.LEAVESTAY,
      attributes
    };

    this.dialog = this.lmModalWrapperService
        .modal(LMDirtyCheckDialogComponent, options)
        .buttons(buttons)
        .title(title)
        .beforeOpen(() => {
          this.lmBusyIndicatorService.hideBusyIndicator();
          return true;
        })
        .afterClose(result => {
          if (result === buttonConfig.first || result === buttonConfig.second) {
            youMayNavigateSource.next(result);
            youMayNavigateSource.complete();
          } else { // Cancel. Stay put!
            youMayNavigateSource.next(LMDialogButtonKey.CANCEL);
            youMayNavigateSource.complete();
          }
        })
        .apply((dialogRef: any) => {
          dialogRef.info = info;
        })
        .open();

    return from(lastValueFrom(youMayNavigate));
  }

  public displayUnsavedChanges(): Promise<boolean> {
    const youMayNavigateSource = new BehaviorSubject<boolean>(false);
    const youMayNavigate: Observable<boolean> = youMayNavigateSource.asObservable();
    const info: LMDialogInfo = { message: Soho.Locale.translate('DocumentHasChangesMessage'), type: LMDialogType.NOYES };  // The document has changes and will be lost if you leave the current document

    this.showUnsavedChanges(info).subscribe((response: UnsavedChangesResponse) => {
      if (response === LMDialogButtonKey.NO) {
        youMayNavigateSource.next(true);
        youMayNavigateSource.complete();
      } else {
        // Cancel. Stay put!
        youMayNavigateSource.next(false);
        youMayNavigateSource.complete();
      }
    });

    return lastValueFrom(youMayNavigate);
  }

  // Handle unsaved changes due to an open modal. Return a boolean Promise that resolves when an async confirmation dialog completes.
  // The boolean indicates if navigation should proceed (true) or be cancelled (false).
  displayNavigationConfirmation(): Promise<boolean> {
    const youMayNavigateSource = new BehaviorSubject<boolean>(false);
    const youMayNavigate: Observable<boolean> = youMayNavigateSource.asObservable();
    const info: LMDialogInfo = { message: Soho.Locale.translate('NavigateAwayMessage'), type: LMDialogType.LEAVESTAY };
    this.showUnsavedChanges(info).subscribe((response: UnsavedChangesResponse) => {
      if (response === LMDialogButtonKey.LEAVE) {
        youMayNavigateSource.next(true);
        youMayNavigateSource.complete();
      } else {
        // Cancel. Stay put!
        youMayNavigateSource.next(false);
        youMayNavigateSource.complete();
      }
    });
    return lastValueFrom(youMayNavigate);
  }

  showBusyIndicator() {
    this.lmBusyIndicatorService.showBusyIndicator();
  }

  hideBusyIndicator() {
    this.lmBusyIndicatorService.hideBusyIndicator();
  }

  // Returns true when there are unsaved changes or an open modal requires confirmation before navigating away.
  checkSaveRequired(fromUnifiedMenu?: boolean): boolean {
    return false;
  }

  // Returns true if at least one modal is open.
  modalOpen() {
    return this.lmModalOpenService.hasOpenModals;
  }

  // Closes all open modals.
  closeModals() {
    this.lmModalOpenService.closeAllModals();
  }

  // Returns true when an open modal requires user confirmation before navigating away.
  // For the Unified menu, prompt whenever a modal is open.
  // TODO: enhance this to perform dirty checks of components inside modals.
  modalNavigationBlocked(fromUnifiedMenu?: boolean) {
    if (fromUnifiedMenu) {
      return this.modalOpen();
    } else {
      return false;
    }
  }

  getStatus(opts: { isDirty?: boolean, hasError?: boolean }): Array<string> {
    const status = new Array<string>();

    // using an array as it affords more flexibility if we want to highlight certain states
    if (opts.isDirty !== undefined) {
      if (opts.isDirty) {
        status.push('dirty');
      } else {
        status.push('pristine');
      }
    }

    if (opts.hasError !== undefined) {
      if (opts.hasError) {
        status.push('invalid');
      } else {
        status.push('valid');
      }
    }

    return status;
  }

  protected get3_5Url(lmHtmlPageUtilService: LMHtmlPageUtilService): string {
    const processUrl: string = document.location.href;
    // if (this.isListSpec(spec)) {
    //   let listSpec: LMListSpec = <LMListSpec>spec;
    //   if (listSpec.listType === 'dashboard') {
    //     const compiledTemplateNavUrl = urltemplate.parse(listSpec.initialDataUrl);
    //     const navigationUrl = compiledTemplateNavUrl.expand({
    //       resourceId:     listSpec.resourceId,
    //       navigationName: listSpec
    //     });
    //     processUrl = navigationUrl;
    //   }
    // }

    return lmHtmlPageUtilService.getHtmlPage(processUrl);
  }

  protected setLegacyVariables(
    has4_0UnSupported: boolean,
    appDisplay: string,
    lmContextService: LMContextService,
    lmHtmlPageUtilService: LMHtmlPageUtilService,
    forceXI?: boolean
  ) {
    // appDisplay is the override flag in settings dialog available only in developer mode.
    // forceXi is coming from handcoded world to open action form dialog.

    // If webapps do not have show using called out, it is read from ProdLineSpec. Both HRT and FSM are locked to use Xi.
    // Pattern test has set show using to Xi too.
    const showUsing = lmContextService.getContext().webAppShowUsing;
    // Developers mostly use showusingxi=true in url and uiharness configuration parameter is not set. This constant evaluates to true (#2 below)
    // QA stacks do not have uiharness configuration parameter set or showusingxi. This constant evaluates to false (#8 below)
    const showUsingXi = lmContextService.isXiFull() || lmContextService.getContext().showusingxi;

    // This is false in pedro
    this.legacyWebUI = (showUsing !== 'SoHo_Xi');

    if (forceXI) {
      // 1.Used in Handcoded world, html-url-doc-template, set to true to open action form dialog.
      this.legacyWebUI = false;
      this.unsupportedPattern = false;
      this.url3_5 = undefined;
    } else if (showUsingXi && !has4_0UnSupported) {
      // 2. All 4x content. Developer sandbox with showUsingXi comes down here
      this.legacyWebUI = false;
      this.unsupportedPattern = false;
      this.url3_5 = undefined;
    } else if (this.uiCompare) {
      // 3. This is hardcoded to false above. This flag is used in form component template html too, but do not know the history.
      this.url3_5 = this.get3_5Url(lmHtmlPageUtilService);
      this.unsupportedPattern = false;
      this.legacyWebUI = true;
    } else if (has4_0UnSupported && (appDisplay !== '3_5')) {
      // 4. QA stacks comes here to initialize 3x URL for unsupported quadrant chart since appDisplay override is undefined in production
      // Developer sandbox also gets down here for unsupported pattern when appDisplay is 'undefined' or '4_0' or 'override'
      this.unsupportedPattern = true;
      this.legacyWebUI = true;
      this.url3_5 = this.get3_5Url(lmHtmlPageUtilService);
    } else if (appDisplay === '4_0' || appDisplay === 'override') {
      // 5. This does not work in developer boxes when showUsingXi is set since we fall into #2 above.
      this.legacyWebUI = false;
      this.url3_5 = undefined;
    } else if (appDisplay === '3_5') {
      // 6. This does not work in developer boxes when showUsingXi is set since we fall into #2 above.
      this.legacyWebUI = true;
      this.url3_5 = this.get3_5Url(lmHtmlPageUtilService);
    } else if (this.legacyWebUI) {
      // 7. Web app definition is 'Soho_Xi' in pedro.
      this.legacyWebUI = true;
      this.url3_5 = this.get3_5Url(lmHtmlPageUtilService);
    } else {
      // 8. QA stacks and developer sandbox with csk.showusingxi not set
      // It gets down here to set the flags.
      this.legacyWebUI = false;
      this.url3_5 = undefined;
    }
    // What matters in pedro is 3x url is correctly initialized for unsupported pattern (quadrant chart)
    // Proliferation of flags made this routine way too complicated
  }

  isPageSpec(arg: any): arg is LMPageSpec {
    return arg && arg.pageSpec;
  }

  isListSpec(arg: any): arg is LMListSpec {
    return arg && arg.busListSpec;
  }

  isFormSpec(arg: any): arg is LMFormSpec {
    return arg && arg.busFormSpec;
  }

  protected doesSpecContainUnsupported4_0Patterns(screenSpec: LMScreenSpec): boolean {
    const retVal = false;
    if (this.isPageSpec(screenSpec)) {
      const pageSpec: LMPageSpec = <LMPageSpec>screenSpec;
      if (pageSpec.resourceId === 'LandingPageWebappPage') {
        return true;
      }
      const panels = pageSpec.pageSpec.panels;

      return this.processPanels4_0ForUnsupported(panels);
    }
    if (this.isListSpec(screenSpec)) {
      const listSpec: LMListSpec = <LMListSpec>screenSpec;
      return this.processList4_0ForUnsupported(listSpec);
    }
    if (this.isFormSpec(screenSpec)) {
      const formSpec: LMFormSpec = <LMFormSpec>screenSpec;
      return this.processForm4_0ForUnsupported(formSpec);
    }
    return false;
  }

  private processList4_0ForUnsupported(listSpec): boolean {
    const charts = listSpec.busListSpec.charts;
    if (charts) {
      const chart: LMChartSpec = listSpec.busListSpec.charts.find(chartSpec => chartSpec.chartType === 'quadrant');
      if (chart) {  // charts[chartName].chartType === 'gap' ||
        return true;
      }
      const listResource = new ListResource({ resourceId: listSpec.resourceId });
      if (listResource.chartName) {
        if (!listSpec?.busListSpec?.charts) {
          return false;
        }
        const chart: LMChartSpec = listSpec.busListSpec.charts.find(chartSpec => chartSpec.id === listResource.chartName);
        if (chart && chart.chartType === 'quadrant') {  // charts[chartName].chartType === 'gap' ||
          return true;
        }
      }
    }
    const detailSections = listSpec.busListSpec.detailSections;
    if (detailSections) {
      const detailSection = listSpec.busListSpec.detailSections;
      for (let i = 0; i < detailSections.length; i++) {
        if (detailSections[ i ].listSpec) {
          const foundUnsupported: boolean = this.processList4_0ForUnsupported(detailSections[ i ].listSpec);
          if (foundUnsupported) {
            return true;
          }
        }
      }
    }
    return false;
  }

  private processForm4_0ForUnsupported(formSpec: LMFormSpec): boolean {
    // if (formSpec.busFormSpec.formType === LMFormType.MATRIX) {
    //   return true;
    // }
    if (formSpec.busFormSpec.formType === LMFormType.COMPOSITE) {
      const panels = formSpec.busFormSpec.panels;
      return this.processPanels4_0ForUnsupported(panels);
    }
    return false;
  }

  public instanceOfLMPagePanelSpec(object: any): object is LMPagePanelSpec {
    return 'id' in object && 'webAppSpecName' in object;
  }

  public instanceOfLMCompositePanelSpec(object: any): object is LMCompositePanelSpec {
    return 'panelId' in object;
  }

  private processPanels4_0ForUnsupported(panelSpecs: Array<LMCompositePanelSpec> | Array<LMPagePanelSpec>): boolean {
    for (let i = 0; i < panelSpecs.length; i++) {
      const panelSpecIn = panelSpecs[ i ];
      if (this.instanceOfLMPagePanelSpec(panelSpecIn)) {
        const panelSpec: LMPagePanelSpec = panelSpecIn;
        if (panelSpec.pane && panelSpec.pane.listSpec) {
          return this.processList4_0ForUnsupported(panelSpec.pane.listSpec);
        }
      } else if (this.instanceOfLMCompositePanelSpec(panelSpecIn)) {
        const panelSpec: LMCompositePanelSpec = panelSpecIn;
        if (panelSpec.panelType === 'formlist') {
          const lSpec: LMListSpec = panelSpec.listSpec;
          if (lSpec) {
            if (this.processList4_0ForUnsupported(lSpec)) {
              return true;
            }
          }
        } else if (panelSpec.panelType === 'form') {
          // no op
        }
      }
    }
  }

  public get componentDestroyed(): boolean {
    return this.m_componentDestroyed;
  }

  protected setComponentDestroyed() {
    this.m_componentDestroyed = true;
  }

  public setUserWebRequestId(id: string) {
    this.userWebRequestId = id;
  }

  // Returns true if the user can run a refresh action for a component from the toolbar.
  public canRefresh(): boolean {
    return false;
  }

  // Performs a data-only refresh for a component.
  public runRefresh(): void {
    // no-op by default
  }

  static isHtmlDocHost(obj: any): boolean {
    return (obj && typeof obj.getHtmlUrlDocService === 'function')
  }
}

export class LMRefreshRequest {
  public constructor(
    public originatingPanelSourceId?: string,
    public originatingSourceId?: string
  ) {
  }
}
